/* eslint-disable @typescript-eslint/no-explicit-any */
import { get, post, put, del } from "@/shared/utils/fetchApi";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";
import { WorkspaceLogoService } from "./workspaceLogoService";

/**
 * Interface para um produto
 */
/** O que o workspace é (AB#86817). */
export type WorkspaceType = "team" | "project";
export const WORKSPACE_TYPES: WorkspaceType[] = ["team", "project"];

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description?: string;
  type?: WorkspaceType | null;
  website?: string;
  logo_url?: string;
  organization_id: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  product_users_count?: number;
}

/**
 * DTO para criação de um produto
 */
export interface CreateWorkspaceDTO {
  name: string;
  slug?: string;
  description?: string;
  type?: WorkspaceType | null;
  website?: string;
  organization_id: string;
  created_by: string;
  logo?: File;
}

/**
 * DTO para atualização de um produto
 */
export interface UpdateWorkspaceDTO {
  name?: string;
  slug?: string;
  description?: string;
  type?: WorkspaceType | null;
  website?: string;
  organization_id?: string;
  logo?: File;
  logo_url?: string;
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas aos produtos
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class WorkspaceService {
  private static readonly BASE_URL = "/products";

  /** Normaliza logo_url (proxy → same-origin) numa lista de workspaces. */
  private static normLogos(list: Workspace[]): Workspace[] {
    return (list ?? []).map((w) => ({
      ...w,
      logo_url: sameOriginProxyUrl(w.logo_url),
    }));
  }

  /**
   * Obtém todos os produtos
   * @param organizationId ID opcional da organização para filtrar
   * @returns Lista de produtos
   */
  static async getAll(organizationId?: string): Promise<Workspace[]> {
    try {
      const url = organizationId
        ? `${this.BASE_URL}?organization_id=${organizationId}`
        : this.BASE_URL;

      return WorkspaceService.normLogos(await get<Workspace[]>(url));
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      throw error;
    }
  }

  /**
   * Obtém todos os produtos com contagem de usuários
   * @param organizationId ID opcional da organização para filtrar
   * @returns Lista de produtos com contagem de usuários
   */
  static async getAllWithUserCount(
    organizationId?: string,
  ): Promise<Workspace[]> {
    try {
      const queryParam = organizationId
        ? `?organization_id=${organizationId}`
        : "";
      const url = `${this.BASE_URL}/with-user-count${queryParam}`;

      return WorkspaceService.normLogos(await get<Workspace[]>(url));
    } catch (error) {
      console.error("Erro ao buscar produtos com contagem de usuários:", error);
      throw error;
    }
  }

  /**
   * Obtém todos os produtos da organização do usuário atual com contagem de usuários
   * Usa o token JWT para identificar o usuário e sua organização
   * @returns Lista de produtos com contagem de usuários
   */
  static async getCurrentOrganizationProducts(): Promise<Workspace[]> {
    try {
      const url = `${this.BASE_URL}/organization/current/with-user-count`;

      return WorkspaceService.normLogos(await get<Workspace[]>(url));
    } catch (error) {
      console.error(
        "[WorkspaceService] Erro ao buscar produtos da organização atual:",
        error,
      );
      // Repassa o ApiError como veio. Embrulhar num Error genérico — mesmo com
      // `cause` — troca o TIPO que o chamador recebe: quem faz `err.status === 403`
      // ou lê `err.code` passa a receber undefined. Só o que não é Error é
      // embrulhado, para o chamador poder contar com um Error sempre.
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Erro ao buscar produtos: ${String(error)}`);
    }
  }

  /**
   * Obtém todos os produtos vinculados a um usuário específico
   * @param userId ID do usuário
   * @returns Lista de produtos vinculados ao usuário
   */
  static async getUserProducts(userId: string): Promise<Workspace[]> {
    try {
      // v2 reestrutura URL: /users/:userId/products (nested REST).
      return WorkspaceService.normLogos(
        await get<Workspace[]>(`/users/${userId}/products`),
      );
    } catch (error) {
      console.error(`Erro ao buscar produtos do usuário ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Obtém um produto pelo ID
   * @param id ID do produto
   * @returns Dados do produto
   */
  static async getById(id: string): Promise<Workspace> {
    try {
      return WorkspaceService.normLogos([
        await get<Workspace>(`${this.BASE_URL}/${id}`),
      ])[0];
    } catch (error) {
      console.error(`Erro ao buscar produto com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Cria um novo produto
   * @param data Dados do produto a ser criado
   * @returns Resposta completa com produto criado e knowledge base
   */
  static async create(data: CreateWorkspaceDTO): Promise<{
    workspace: Workspace;
    knowledgeBase?: any;
  }> {
    try {
      // org vem do token (created_by/organization_id ignorados pelo
      // controller). Logo sobe separado (POST /products/new/logo) → logo_url,
      // já que o store v2 aceita só JSON. Resposta é o produto raw (sem
      // envelope nem knowledgeBase — depende de #knowledge portado).
      let logoUrl: string | undefined;
      if (data.logo) {
        logoUrl = await WorkspaceLogoService.uploadWorkspaceLogo(
          data.logo,
          undefined,
        );
      }
      const workspace = await post<Workspace>(this.BASE_URL, {
        name: data.name,
        slug: data.slug,
        description: data.description,
        type: data.type,
        website: data.website,
        logo_url: logoUrl,
      });
      return { workspace, knowledgeBase: undefined };
    } catch (error) {
      console.error("[WorkspaceService] Erro ao criar produto:", error);
      throw error;
    }
  }

  /**
   * Atualiza um produto existente
   * @param id ID do produto
   * @param data Dados a serem atualizados
   * @returns Produto atualizado
   */
  static async update(
    id: string,
    data: UpdateWorkspaceDTO,
  ): Promise<Workspace> {
    try {
      // logo sobe separado (POST /products/:id/logo) → logo_url; o update
      // v2 (PUT /products/:id) aceita só JSON e retorna o produto raw. `slug`
      // não existe no validator v2 — ignorado pelo backend.
      let logoUrl = data.logo_url;
      if (data.logo) {
        logoUrl = await WorkspaceLogoService.uploadWorkspaceLogo(data.logo, id);
      }
      return await put<Workspace>(`${this.BASE_URL}/${id}`, {
        name: data.name,
        description: data.description,
        type: data.type,
        website: data.website,
        logo_url: logoUrl,
      });
    } catch (error) {
      console.error(`Erro ao atualizar produto com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remove um produto existente e todas as suas dependências
   * @param id ID do produto
   * @returns Detalhes da operação de exclusão
   */
  static async delete(id: string): Promise<{
    success: boolean;
    message: string;
    details?: {
      productDeleted: boolean;
      userAssociationsRemoved: boolean;
      knowledgeBasesRemoved: boolean;
      storageRemoved?: boolean;
      agentVersionsRemoved?: boolean;
      agentBaseRemoved?: boolean;
      chatHistoryRemoved?: boolean;
      errors?: string[];
    };
  }> {
    try {
      // v2 retorna 204 No Content em sucesso; throws ApiError com code
      // E_PRODUCT_DELETION_BLOCKED (409) quando há dependências.
      // NOTA: `details` (knowledgeBasesRemoved/agentBaseRemoved/...) sempre
      // undefined sob v2 — depende de #knowledge/#agents portados.
      await del<void>(`${this.BASE_URL}/${id}`);
      return { success: true, message: "Produto removido com sucesso" };
    } catch (error: any) {
      console.error(
        `[WorkspaceService] Erro ao excluir produto com ID ${id}:`,
        error,
      );

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erro desconhecido ao excluir produto";

      return {
        success: false,
        message: errorMessage,
      };
    }
  }
}
